// C01/C02/C03/C05/C08/C10/C11/C12 — GTM assessments, planners and kits.
import { useMemo, useState } from "react";
import { gateScore, weightedMatrix, territoryCoverage, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, ToolSection, Note, useNumbers, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button type="button" data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4">
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- C01 Market Entry Go/No-Go ---------- */
const C01_GATES = [
  { key: "demand", label: "Demand evidence", hint: "Real buyer behaviour observed, not a market report.", hardStop: true, threshold: 3 },
  { key: "economics", label: "Unit economics", hint: "Contribution positive at realistic volumes.", hardStop: true, threshold: 3 },
  { key: "channel", label: "Channel access", hint: "A named, reachable path to the buyer.", hardStop: true, threshold: 3 },
  { key: "regulatory", label: "Regulatory & compliance", hint: "Licences, labels, certifications in hand or dated.", hardStop: true, threshold: 2 },
  { key: "team", label: "Team coverage", hint: "A named owner with time, not a shared inbox.", hardStop: false },
  { key: "capital", label: "Capital to survive the ramp", hint: "18 months of funding for the entry case, not the plan case.", hardStop: false },
];
export function ToolC01() {
  const [scores, setScores] = useState({ demand: 2, economics: 3, channel: 2, regulatory: 4, team: 3, capital: 3 });
  const r = useMemo(() => gateScore(C01_GATES.map((g) => ({ ...g, score: scores[g.key] }))), [scores]);
  const verdicts = { go: ["GO — proceed with a dated plan", "good"], conditional: ["CONDITIONAL — close the weak gates first", "warn"], "no-go": ["NO-GO — a hard-stop gate failed", "bad"] };
  const [vText, vTone] = verdicts[r.verdict];
  return (
    <div data-testid="tool-C01" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-6">
        {C01_GATES.map((g) => (
          <div key={g.key} className="border border-zinc-200 p-4" data-testid={`c01-gate-${g.key}`}>
            <SliderField label={`${g.label}${g.hardStop ? " (hard stop)" : ""}`} hint={g.hint} value={scores[g.key]} onChange={(v) => setScores((s) => ({ ...s, [g.key]: v }))} min={0} max={5} suffix="/5" testid={`c01-${g.key}`} />
          </div>
        ))}
      </div>
      <div>
        <div data-testid="c01-result">
          <ResultRow label="Overall readiness" value={`${r.scorePct}%`} />
          <ResultRow label="Verdict" value={vText} strong tone={vTone === "bad" ? "bad" : vTone === "good" ? "good" : undefined} />
          {r.failedGates.length > 0 && (
            <div className="mt-4" data-testid="c01-failed">
              <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Failed hard-stop gates</div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-red-700">{r.failedGates.map((g) => <li key={g}>{g}</li>)}</ul>
            </div>
          )}
        </div>
        <Note tone="warn">Hard stops exist because some weaknesses can't be averaged away — a 5/5 team cannot compensate for 1/5 unit economics. Write the decision memo before the launch budget moves.</Note>
      </div>
    </div>
  );
}

/* ---------- C02 Launch Pre-Mortem (30 authored failure cards) ---------- */
const C02_CARDS = [
  { cat: "Product", cards: ["The hero SKU underperforms its concept test in real kitchens/hands.", "A quality complaint trends in week 3 and response takes 10 days.", "The pack fails in transit — damage claims eat the launch margin.", "Shelf-life is shorter in trade conditions than in the lab.", "The variant chosen as lead is the one early buyers like least."] },
  { cat: "Pricing", cards: ["The launch price lands 15% above the reference brand and nobody trades up.", "Intro discounts train buyers to wait for deals.", "Trade margins are too thin to earn push.", "A competitor undercuts within the first month.", "Price-pack architecture confuses the shelf — wrong grammage for the mission."] },
  { cat: "Distribution", cards: ["Listings happen but replenishment fails — OOS in week 2.", "The distributor prioritises their bigger brand.", "Coverage targets are hit on paper, dead stock in reality.", "Modern trade slotting is paid but the planogram never lands.", "E-commerce content is live but ratings start below 3.5."] },
  { cat: "Demand", cards: ["Trial targets met via sampling; repeat never arrives.", "The campaign wins awards and the SKU wins nothing.", "The real buyer is not the persona the media plan targeted.", "Word of mouth is negative on the one attribute we compromised.", "Seasonality was misread — launch lands in the slow quarter."] },
  { cat: "Execution", cards: ["Sales and supply chain planned for different volumes.", "The launch team is pulled onto a fire in the core business.", "First-production-run quality drifts from the pilot batch.", "Approval cycles add 6 weeks; the window closes.", "Nobody owns the week-4 repeat-rate review."] },
  { cat: "Competitive", cards: ["The leader responds with a flanker at our exact price point.", "A me-too launches within 90 days with better trade terms.", "Retailers demand better margins citing a rival's offer.", "Our claim is challenged — legally or on social.", "The category itself starts declining as we enter."] },
];
export function ToolC02() {
  const [sel, setSel] = useState([]);
  const [mitigations, setMitigations] = useState({});
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const all = C02_CARDS.flatMap((c, ci) => c.cards.map((card, qi) => ({ id: `${ci}-${qi}`, cat: c.cat, card })));
  const chosen = all.filter((c) => sel.includes(c.id));
  return (
    <div data-testid="tool-C02">
      <p className="text-sm text-zinc-600 mb-5">It's 12 months from now and the launch failed. Pick the ways it happened — then write the mitigation before launch, not after.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {C02_CARDS.map((c, ci) => (
          <div key={c.cat}>
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">{c.cat}</div>
            <div className="space-y-2">
              {c.cards.map((card, qi) => {
                const id = `${ci}-${qi}`;
                const on = sel.includes(id);
                return (
                  <button key={id} type="button" onClick={() => toggle(id)} data-testid={`c02-card-${id}`}
                    className={`block w-full text-left border p-3 text-[13px] leading-snug transition-colors ${on ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                    {card}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {chosen.length > 0 && (
        <ToolSection title={`Your risk register (${chosen.length}) — write the mitigation`} testid="c02-register">
          <div className="space-y-3">
            {chosen.map((c) => (
              <div key={c.id} className="border border-zinc-200 p-4 grid md:grid-cols-2 gap-3" data-testid={`c02-risk-${c.id}`}>
                <div><span className="text-xs uppercase tracking-wide text-zinc-400">{c.cat}</span><p className="text-sm text-zinc-800 mt-1">{c.card}</p></div>
                <textarea value={mitigations[c.id] || ""} onChange={(e) => setMitigations((s) => ({ ...s, [c.id]: e.target.value }))} rows={2} placeholder="Mitigation / trigger / owner…" className="border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`c02-mit-${c.id}`} />
              </div>
            ))}
          </div>
          <div className="mt-4"><CopyButton testid="c02-copy" getText={() => `LAUNCH PRE-MORTEM — RISK REGISTER\n\n${chosen.map((c, i) => `${i + 1}. [${c.cat}] ${c.card}\n   Mitigation: ${mitigations[c.id] || "TBD"}`).join("\n")}`} /></div>
        </ToolSection>
      )}
    </div>
  );
}

/* ---------- C03 Route-to-Market Decision Engine ---------- */
const C03_CHANNELS = ["Direct sales force", "Distributor / GT", "Modern trade", "D2C website", "Marketplaces"];
const C03_CRITERIA = [
  { key: "margin", label: "Margin retained" }, { key: "speed", label: "Speed to scale" },
  { key: "control", label: "Brand & price control" }, { key: "capital", label: "Low capital need" }, { key: "fit", label: "Fit to our product" },
];
export function ToolC03() {
  const [weights, setWeights] = useState({ margin: 2, speed: 2, control: 1, capital: 2, fit: 3 });
  const [scores, setScores] = useState({
    "Direct sales force": { margin: 5, speed: 1, control: 5, capital: 1, fit: 3 },
    "Distributor / GT": { margin: 3, speed: 4, control: 2, capital: 4, fit: 4 },
    "Modern trade": { margin: 2, speed: 3, control: 3, capital: 3, fit: 3 },
    "D2C website": { margin: 5, speed: 2, control: 5, capital: 2, fit: 3 },
    "Marketplaces": { margin: 2, speed: 5, control: 2, capital: 3, fit: 4 },
  });
  const ranked = useMemo(() => weightedMatrix({ items: C03_CHANNELS.map((n) => ({ name: n, scores: scores[n] })), criteria: C03_CRITERIA, weights }), [scores, weights]);
  return (
    <div data-testid="tool-C03" className="grid lg:grid-cols-5 gap-10">
      <div className="lg:col-span-3">
        <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Score each channel 0–5 against your reality</div>
        <div className="space-y-5">
          {C03_CHANNELS.map((n) => (
            <div key={n} className="border border-zinc-200 p-4" data-testid={`c03-${n.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="text-sm font-medium">{n}</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                {C03_CRITERIA.map((c) => (
                  <SliderField key={c.key} label={c.label} value={scores[n][c.key]} onChange={(v) => setScores((s) => ({ ...s, [n]: { ...s[n], [c.key]: v } }))} min={0} max={5} suffix="" testid={`c03-${n.slice(0, 6).replace(/\W/g, "")}-${c.key}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Your current priorities</div>
        <div className="space-y-4">
          {C03_CRITERIA.map((c) => (
            <SliderField key={c.key} label={c.label} value={weights[c.key]} onChange={(v) => setWeights((w) => ({ ...w, [c.key]: v }))} min={0} max={5} suffix="" testid={`c03-w-${c.key}`} />
          ))}
        </div>
        <div className="mt-8 border-t-2 border-black pt-6" data-testid="c03-result">
          {ranked.map((r) => (
            <ResultRow key={r.name} label={`#${r.rank} ${r.name}`} value={`${r.score}%`} strong={r.rank === 1} tone={r.rank === 1 ? "good" : undefined} />
          ))}
        </div>
        {ranked.length > 1 && ranked[0].score - ranked[1].score <= 2 ? (
          <Note tone="warn">Too close to call — {ranked[0].name} and {ranked[1].name} are within 2 points. Pilot both for 90 days with explicit kill criteria, then commit.</Note>
        ) : (
          <Note>The winner is your primary route for 18 months. Most launches fail from channel polygamy — two routes half-built lose to one route done properly.</Note>
        )}
      </div>
    </div>
  );
}

/* ---------- C05 Distributor Performance Scorecard ---------- */
const C05_DIMS = [
  { key: "sales", label: "Sales vs target", hint: "Secondary, not primary." },
  { key: "distribution", label: "Numeric distribution growth", hint: "New live outlets per quarter." },
  { key: "payments", label: "Payment discipline", hint: "On-time % and cheque/RTGS hygiene." },
  { key: "infra", label: "Infrastructure & coverage", hint: "Vans, godown, beat discipline." },
  { key: "development", label: "Market development", hint: "Displays, visibility, new-category push." },
  { key: "data", label: "Data sharing", hint: "Stock & secondary reported weekly." },
];
export function ToolC05() {
  const [v, setV] = useState({ sales: 3, distribution: 2, payments: 4, infra: 3, development: 2, data: 1 });
  const [name, setName] = useState("Distributor A");
  const w = { sales: 3, distribution: 2, payments: 2, infra: 1, development: 2, data: 1 };
  const score = useMemo(() => {
    const wsum = Object.values(w).reduce((s, x) => s + x, 0);
    return Math.round((C05_DIMS.reduce((s, d) => s + v[d.key] * w[d.key], 0) / (wsum * 5)) * 100);
  }, [v]);
  const band = score >= 75 ? ["Invest & expand", "good"] : score >= 55 ? ["Develop — set a 90-day JBP", undefined] : score >= 35 ? ["Watch — named risks", "warn"] : ["Replace or restructure", "bad"];
  return (
    <div data-testid="tool-C05" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Distributor name" type="text" value={name} onChange={setName} testid="c05-name" />
        {C05_DIMS.map((d) => (
          <div key={d.key} className="border border-zinc-200 p-4" data-testid={`c05-dim-${d.key}`}>
            <SliderField label={d.label} hint={d.hint} value={v[d.key]} onChange={(x) => setV((s) => ({ ...s, [d.key]: x }))} min={0} max={5} suffix="/5" testid={`c05-${d.key}`} />
          </div>
        ))}
      </div>
      <div>
        <div data-testid="c05-result">
          <ResultRow label={`${name} health score`} value={`${score}/100`} strong tone={band[1] === "bad" ? "bad" : band[1] === "good" ? "good" : undefined} />
          <ResultRow label="Verdict" value={band[0]} />
          <ResultRow label="Risk gate — data sharing" value={v.data <= 1 ? "FAIL — you are blind on secondary" : "Pass"} tone={v.data <= 1 ? "bad" : "good"} />
          <ResultRow label="Risk gate — payments" value={v.payments <= 1 ? "FAIL — credit risk" : "Pass"} tone={v.payments <= 1 ? "bad" : "good"} />
        </div>
        <ToolSection title="Next JBP agenda (auto-suggested)">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600" data-testid="c05-jbp">
            {C05_DIMS.filter((d) => v[d.key] <= 2).map((d) => <li key={d.key}>{d.label}: agree one measurable 90-day move.</li>)}
            {C05_DIMS.every((d) => v[d.key] > 2) && <li>No weak dimensions — discuss expansion: new town coverage or a second category.</li>}
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}

/* ---------- C08 Territory Coverage Optimizer ---------- */
export function ToolC08() {
  const [v, set] = useNumbers({ outlets: 500, visitsPerMonth: 2, visitMinutes: 30, travelMinutesPerDay: 90, workDaysPerMonth: 24, workHoursPerDay: 8, currentReps: 2 });
  const r = useMemo(() => territoryCoverage(v), [v]);
  return (
    <div data-testid="tool-C08" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Active outlets" value={v.outlets} onChange={set("outlets")} testid="c08-outlets" />
          <Field label="Required visits / outlet / month" value={v.visitsPerMonth} onChange={set("visitsPerMonth")} testid="c08-freq" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Minutes per visit" value={v.visitMinutes} onChange={set("visitMinutes")} testid="c08-minutes" />
          <Field label="Travel minutes / day" value={v.travelMinutesPerDay} onChange={set("travelMinutesPerDay")} testid="c08-travel" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Work days / month" value={v.workDaysPerMonth} onChange={set("workDaysPerMonth")} testid="c08-days" />
          <Field label="Work hours / day" value={v.workHoursPerDay} onChange={set("workHoursPerDay")} testid="c08-hours" />
        </div>
        <Field label="Current field reps" value={v.currentReps} onChange={set("currentReps")} testid="c08-reps" />
      </div>
      <div>
        <div data-testid="c08-result">
          <ResultRow label="Visits required / month" value={r.totalVisitsPerMonth.toLocaleString("en-IN")} />
          <ResultRow label="Selling hours required" value={r.visitHoursRequired.toLocaleString("en-IN")} />
          <ResultRow label="Effective capacity / rep / month" value={`${r.capacityHoursPerRep} hrs`} />
          <ResultRow label="Reps required" value={`${r.requiredReps} (round up: ${r.requiredRepsCeil})`} strong />
          <ResultRow label="Verdict" value={r.verdict} tone={r.verdict === "under-covered" ? "bad" : "good"} />
        </div>
        <Note tone={r.verdict === "under-covered" ? "warn" : "info"}>
          {r.verdict === "under-covered"
            ? `Short by ~${Math.abs(r.gap)} reps — the gap shows up as missed beats and declining numeric distribution, not as a line in any report.`
            : r.verdict === "over-staffed" ? "More capacity than the beat needs — rebalance territories before adding outlets." : "Coverage matches workload. Now audit visit quality, not quantity."}
        </Note>
      </div>
    </div>
  );
}

/* ---------- C10 Distributor Selection Due Diligence ---------- */
const C10_DOCS = ["GST certificate & registration", "FSSAI / trade licences (as applicable)", "Bank statement — 6 months", "Godown ownership/lease papers", "Existing brand list with contactable references", "Vehicle & staff roster", "Territory map with current beat plan"];
const C10_PLAN = [
  ["Days 1–10", "Joint market visit; agree beat plan, outlet master and credit policy in writing."],
  ["Days 11–20", "First order sized to 3 weeks of realistic secondary — not the target."],
  ["Days 21–30", "Weekly rhythm set: secondary report, claims process, review call. Baseline photo of every display."],
];
export function ToolC10() {
  const [candidates, setCandidates] = useState([
    { name: "Candidate 1", scores: { finance: 3, infra: 4, reputation: 3, portfolio: 2, hunger: 4 } },
    { name: "Candidate 2", scores: { finance: 4, infra: 3, reputation: 4, portfolio: 3, hunger: 2 } },
  ]);
  const crit = [{ key: "finance", label: "Financial strength" }, { key: "infra", label: "Infrastructure" }, { key: "reputation", label: "Market reputation" }, { key: "portfolio", label: "Portfolio fit (no conflicts)" }, { key: "hunger", label: "Hunger for your category" }];
  const ranked = useMemo(() => weightedMatrix({ items: candidates, criteria: crit, weights: { finance: 3, infra: 2, reputation: 2, portfolio: 2, hunger: 3 } }), [candidates]);
  const [docs, setDocs] = useState({});
  return (
    <div data-testid="tool-C10">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-5">
          {candidates.map((c, i) => (
            <div key={i} className="border border-zinc-200 p-4" data-testid={`c10-cand-${i}`}>
              <input value={c.name} onChange={(e) => setCandidates((s) => s.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black pb-1" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                {crit.map((k) => (
                  <SliderField key={k.key} label={k.label} value={c.scores[k.key]} onChange={(v) => setCandidates((s) => s.map((x, xi) => (xi === i ? { ...x, scores: { ...x.scores, [k.key]: v } } : x)))} min={0} max={5} suffix="" testid={`c10-${i}-${k.key}`} />
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setCandidates((s) => [...s, { name: `Candidate ${s.length + 1}`, scores: { finance: 3, infra: 3, reputation: 3, portfolio: 3, hunger: 3 } }])} className="text-sm font-medium underline underline-offset-4" data-testid="c10-add">+ Add candidate</button>
          <div className="border-t-2 border-black pt-6" data-testid="c10-result">
            {ranked.map((r) => <ResultRow key={r.name} label={`#${r.rank} ${r.name}`} value={`${r.score}%`} strong={r.rank === 1} />)}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Document checklist — verify before signing</div>
          <div className="space-y-2" data-testid="c10-docs">
            {C10_DOCS.map((d, i) => (
              <label key={d} className="flex items-center gap-3 border border-zinc-200 p-3 text-sm cursor-pointer hover:border-black transition-colors" data-testid={`c10-doc-${i}`}>
                <input type="checkbox" checked={!!docs[i]} onChange={() => setDocs((s) => ({ ...s, [i]: !s[i] }))} className="w-4 h-4 accent-black" />
                {d}
              </label>
            ))}
          </div>
          <ToolSection title="First 30 days — onboarding plan">
            <div className="space-y-3">
              {C10_PLAN.map(([phase, text]) => (
                <div key={phase} className="flex gap-4 text-sm"><span className="w-24 shrink-0 font-medium text-black">{phase}</span><span className="text-zinc-600">{text}</span></div>
              ))}
            </div>
          </ToolSection>
        </div>
      </div>
    </div>
  );
}

/* ---------- C11 Dealer Pulse Survey ---------- */
const C11_QS = [
  { q: "How easy is it to do business with us? (1–5)", theme: "Ease of business" },
  { q: "Do our schemes reach you fully and on time? (1–5)", theme: "Scheme delivery" },
  { q: "Are claims settled within 30 days? (1–5)", theme: "Claims" },
  { q: "Does our salesperson visit on schedule? (1–5)", theme: "Coverage" },
  { q: "How confident are you recommending our brand? (1–5)", theme: "Advocacy" },
  { q: "Which one thing should we fix first? (open)", theme: "Priorities" },
];
export function ToolC11() {
  const [counts, setCounts] = useState({}); // qIdx -> [c1..c5]
  const bump = (qi, score, d) => setCounts((s) => {
    const arr = [...(s[qi] || [0, 0, 0, 0, 0])];
    arr[score - 1] = Math.max(0, arr[score - 1] + d);
    return { ...s, [qi]: arr };
  });
  return (
    <div data-testid="tool-C11">
      <p className="text-sm text-zinc-600 mb-5">A six-question pulse your dealers answer in 90 seconds — run it quarterly by WhatsApp or phone, then tally responses below.</p>
      <div className="grid md:grid-cols-2 gap-4" data-testid="c11-questions">
        {C11_QS.map((item, i) => (
          <div key={i} className="border border-zinc-200 p-4">
            <div className="text-sm font-medium">{i + 1}. {item.q}</div>
            <div className="text-xs text-zinc-400 mt-0.5">Theme: {item.theme}</div>
            {item.q.includes("1–5") ? (
              <div className="mt-3 flex items-center gap-2" data-testid={`c11-q-${i}`}>
                {[1, 2, 3, 4, 5].map((sc) => (
                  <div key={sc} className="flex flex-col items-center">
                    <button type="button" onClick={() => bump(i, sc, 1)} className="w-8 h-8 border border-zinc-300 text-xs hover:border-black" data-testid={`c11-q${i}-s${sc}`}>{sc}</button>
                    <span className="text-[11px] tabular-nums text-zinc-500 mt-0.5">{(counts[i] || [0, 0, 0, 0, 0])[sc - 1]}</span>
                  </div>
                ))}
              </div>
            ) : (
              <textarea rows={2} placeholder="Log the top 3 verbatim answers here…" className="mt-3 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`c11-open-${i}`} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 max-w-lg" data-testid="c11-result">
        {C11_QS.filter((x) => x.q.includes("1–5")).map((item, qi) => {
          const arr = counts[qi] || [0, 0, 0, 0, 0];
          const n = arr.reduce((s, v) => s + v, 0);
          const avg = n ? (arr.reduce((s, v, ix) => s + v * (ix + 1), 0) / n).toFixed(1) : "—";
          return <ResultRow key={item.theme} label={`${item.theme} (${n} responses)`} value={avg === "—" ? "—" : `${avg} / 5`} tone={avg !== "—" && Number(avg) < 3 ? "bad" : undefined} />;
        })}
      </div>
      <Note>Translate the questions into your dealers' working language before sending — a survey in the wrong language measures English, not sentiment. Share the fixes you ship back to dealers; response rates double when they see action.</Note>
    </div>
  );
}

/* ---------- C12 90-Day New-Market Rollout ---------- */
const C12_WEEKS = [
  [1, "Sign distributor/partner; freeze beat plan and credit terms", "Partner signed, terms in writing"],
  [2, "Outlet master built — top 500 by potential, mapped", "Outlet master live"],
  [3, "First order placed (3 weeks of realistic secondary)", "Order dispatched"],
  [4, "Stock-in verified; display & price norms photographed", "100% stocking confirmed"],
  [5, "Sales team beat discipline begins; daily reporting starts", "Beat adherence ≥ 90%"],
  [6, "First secondary review; plug OOS; claims process tested", "Secondary report #1"],
  [7, "Visibility wave 1 in top 100 outlets", "Displays photographed"],
  [8, "GATE: repeat orders vs plan — proceed / fix / pause", "Go/no-go minuted"],
  [9, "Extend to next 250 outlets if gate passed", "Numeric distribution +25%"],
  [10, "First consumer activation in 2 high-traffic clusters", "Activation report"],
  [11, "Distributor JBP review; reset Q2 targets", "JBP signed"],
  [12, "Claims & credit hygiene audit", "Zero claims > 30 days"],
  [13, "GATE: 90-day review — scale, iterate or exit", "Decision minuted with evidence"],
];
export function ToolC12() {
  const [market, setMarket] = useState("New market");
  const [done, setDone] = useState({});
  const [custom, setCustom] = useState({});
  const completed = Object.values(done).filter(Boolean).length;
  return (
    <div data-testid="tool-C12">
      <div className="max-w-sm"><Field label="Market name" type="text" value={market} onChange={setMarket} testid="c12-market" /></div>
      <div className="mt-6 space-y-2" data-testid="c12-plan">
        {C12_WEEKS.map(([wk, task, kpi]) => (
          <div key={wk} className={`border p-4 grid md:grid-cols-12 gap-3 items-center transition-colors ${done[wk] ? "border-black bg-zinc-50" : "border-zinc-200"}`} data-testid={`c12-week-${wk}`}>
            <div className="md:col-span-1 flex items-center gap-2">
              <input type="checkbox" checked={!!done[wk]} onChange={() => setDone((s) => ({ ...s, [wk]: !s[wk] }))} className="w-4 h-4 accent-black" data-testid={`c12-done-${wk}`} />
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">W{wk}</span>
            </div>
            <div className="md:col-span-7">
              <input value={custom[wk] ?? task} onChange={(e) => setCustom((s) => ({ ...s, [wk]: e.target.value }))} className="w-full bg-transparent text-sm outline-none border-b border-transparent focus:border-black" data-testid={`c12-task-${wk}`} />
            </div>
            <div className="md:col-span-4 text-xs text-zinc-500">{kpi}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 max-w-md" data-testid="c12-result">
        <ResultRow label={`${market} rollout progress`} value={`${completed} / 13 weeks ticked`} strong={completed === 13} />
      </div>
      <Note tone="warn">Weeks 8 and 13 are gates, not rituals. If repeat orders are below plan at week 8, the correct move is to fix distribution before spending on activation — the calendar gives you permission to say it.</Note>
    </div>
  );
}
